import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Target,
  Users,
  Globe,
  DollarSign,
  Settings,
  LifeBuoy,
  LogOut,
  Inbox,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Progress } from '@/components/ui/Progress';
import { getInitials } from '@/lib/utils';
import { DashboardBackground } from '@/components/three';
import { PLAN_CONFIGS } from '@shared/types';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Campaigns', href: '/dashboard/campaigns', icon: Target },
  { name: 'Leads', href: '/dashboard/leads', icon: Users },
  { name: 'Sites', href: '/dashboard/sites', icon: Globe },
  { name: 'Earnings', href: '/dashboard/earnings', icon: DollarSign },
  { name: 'Settings', href: '/dashboard/settings', icon: Settings },
  { name: 'Support', href: '/dashboard/support', icon: LifeBuoy },
];

// Shown only to users with the `admin` custom claim.
const adminNavigation = [
  { name: 'Support Inbox', href: '/dashboard/support-inbox', icon: Inbox },
];

function SidebarContent({
  userData,
  quotaUsed,
  quotaLimit,
  quotaPercentage,
  handleSignOut,
  onNavClick,
}: {
  userData: ReturnType<typeof useAuth>['userData'];
  quotaUsed: number;
  quotaLimit: number;
  quotaPercentage: number;
  handleSignOut: () => void;
  onNavClick?: () => void;
}) {
  const isAdmin = useIsAdmin();
  const items = isAdmin ? [...navigation, ...adminNavigation] : navigation;

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg border-2 border-white bg-black flex items-center justify-center overflow-hidden">
            <img src="/wolfromulus.png" alt="Romulus" className="w-8 h-8 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-white font-roman tracking-wider">ROMVLVS</h1>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === '/dashboard'}
            onClick={onNavClick}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all',
                isActive
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              )
            }
          >
            <item.icon className="w-5 h-5" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="glass-card p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Sites Created</span>
            <span className="text-sm font-medium text-white">
              {quotaUsed}/{quotaLimit}
            </span>
          </div>
          <Progress value={quotaPercentage} />
          <p className="text-xs text-gray-500 mt-2">
            {userData?.planId ? PLAN_CONFIGS[userData.planId].name : 'Starter'} Plan
          </p>
        </div>

        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
          <Avatar>
            <AvatarFallback>
              {getInitials(userData?.fullName || 'User')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {userData?.fullName || 'User'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {userData?.companyName || 'No company'}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleSignOut}>
            <LogOut className="w-4 h-4 text-gray-400" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function DashboardLayout() {
  const { userData, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const quotaUsed = userData?.usage?.sitesUsed ?? 0;
  const quotaLimit = userData?.quotas?.sitesLimit ?? 10;
  const quotaPercentage = Math.min((quotaUsed / quotaLimit) * 100, 100);

  return (
    <DashboardBackground>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Sidebar */}
        <div
          style={{
            width: '288px',
            minWidth: '288px',
            backgroundColor: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(20px)',
            borderRight: '1px solid rgba(91,58,58,0.15)',
            display: 'flex',
            flexDirection: 'column',
            position: 'sticky',
            top: 0,
            height: '100vh',
            zIndex: 20,
          }}
        >
          <SidebarContent
            userData={userData}
            quotaUsed={quotaUsed}
            quotaLimit={quotaLimit}
            quotaPercentage={quotaPercentage}
            handleSignOut={handleSignOut}
            onNavClick={() => {}}
          />
        </div>

        {/* Main content */}
        <div className="animate-page-in" style={{ flex: 1, padding: '24px', position: 'relative', zIndex: 10 }}>
          <Outlet />
        </div>
      </div>
    </DashboardBackground>
  );
}
